import { buildSiteTree, SITE_KINDS, type Site, type SiteKind, type SiteNode } from "@fts/core";
import { useMemo, useState, type FormEvent } from "react";
import { ConfirmDialog, Dialog, FormError, FormField, fieldAria } from "../components/Dialog";
import { useFeedback } from "../components/Toast";
import { Note } from "../components/ui";
import { useI18n } from "../i18n";
import { descendantIds } from "../lib/fleet";
import { isTenantAdmin } from "../lib/permissions";
import { navigate } from "../router/hash";
import { useSession, type TenantData } from "../state/session";

/** Sites in tree order with an indented label (for parent and site selects). */
export function siteOptions(sites: Site[], exclude: Set<string> = new Set()): Array<{ id: string; label: string }> {
  const out: Array<{ id: string; label: string }> = [];
  const walk = (nodes: SiteNode[], depth: number) => {
    for (const n of nodes) {
      if (exclude.has(n.id)) continue;
      out.push({ id: n.id, label: `${" ".repeat(depth)}${n.name}` });
      walk(n.children, depth + 1);
    }
  };
  walk(buildSiteTree(sites, []), 0);
  return out;
}

/** Parse an optional decimal coordinate ("40,77" or "40.77"); undefined = invalid. */
export function parseCoord(raw: string, limit: number): number | null | undefined {
  const s = raw.trim().replace(",", ".");
  if (!s) return null;
  if (!/^-?\d+(\.\d+)?$/.test(s)) return undefined;
  const n = Number(s);
  return Math.abs(n) <= limit ? n : undefined;
}

export function SiteFormDialog({
  data,
  site,
  defaultParentId,
  onClose,
}: {
  data: TenantData;
  /** Edit when given, create otherwise. */
  site?: Site;
  defaultParentId?: string | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { provider, tenant, role, refresh } = useSession();
  const feedback = useFeedback();
  const admin = isTenantAdmin(role);
  // Fleet managers can neither create root sites nor turn a site into one.
  const rootAllowed = admin || (site !== undefined && site.parentId === null);
  const initialParent = site ? site.parentId : (defaultParentId ?? (admin ? null : (data.sites[0]?.id ?? null)));
  const [name, setName] = useState(site?.name ?? "");
  const [kind, setKind] = useState<SiteKind>(site?.kind ?? (initialParent ? "warehouse" : "site"));
  const [parentId, setParentId] = useState<string | null>(initialParent);
  const [address, setAddress] = useState(site?.address ?? "");
  const [lat, setLat] = useState(site?.lat != null ? String(site.lat) : "");
  const [lon, setLon] = useState(site?.lon != null ? String(site.lon) : "");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const exclude = useMemo(() => (site ? descendantIds(data.sites, site.id) : new Set<string>()), [data.sites, site]);
  const parents = useMemo(() => siteOptions(data.sites, exclude), [data.sites, exclude]);
  const lockedRoot = site !== undefined && site.parentId === null && !admin;

  const latV = parseCoord(lat, 90);
  const lonV = parseCoord(lon, 180);
  const errors = {
    name: !name.trim() ? t("form.required") : name.trim().length > 120 ? t("form.tooLong", { max: 120 }) : null,
    parent: !rootAllowed && !parentId ? t("site.parentRequired") : null,
    lat: latV === undefined ? t("site.latInvalid") : latV === null && lonV != null ? t("site.coordPair") : null,
    lon: lonV === undefined ? t("site.lonInvalid") : lonV === null && latV != null ? t("site.coordPair") : null,
  };
  const shown = (k: keyof typeof errors) => (touched ? errors[k] : null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!tenant || Object.values(errors).some(Boolean)) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await provider.saveSite({
        id: site?.id,
        tenantId: tenant.id,
        parentId,
        name: name.trim(),
        kind,
        address: address.trim() || null,
        lat: latV ?? null,
        lon: lonV ?? null,
      });
      await refresh();
      feedback.ok(t(site ? "toast.savedNamed" : "toast.addedNamed", { name: saved.name }));
      onClose();
      if (!site) navigate(`/sites/${encodeURIComponent(saved.id)}`);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={site ? t("siteForm.editTitle") : t("siteForm.createTitle")}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? t("common.working") : t("common.save")}
          </button>
        </>
      }
    >
      <FormField id="site-name" label={t("site.name")} error={shown("name")}>
        <input {...fieldAria("site-name", { error: shown("name") })} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} data-autofocus />
      </FormField>
      <div className="form-row">
        <FormField id="site-kind" label={t("site.kind")}>
          <select id="site-kind" value={kind} onChange={(e) => setKind(e.target.value as SiteKind)}>
            {SITE_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`siteKind.${k}`)}
              </option>
            ))}
          </select>
        </FormField>
        <FormField id="site-parent" label={t("site.parent")} error={shown("parent")} hint={lockedRoot ? t("site.rootLocked") : !admin ? t("site.parentHintManager") : undefined}>
          <select
            {...fieldAria("site-parent", { error: shown("parent"), hint: lockedRoot || !admin })}
            value={parentId ?? ""}
            onChange={(e) => setParentId(e.target.value || null)}
            disabled={lockedRoot}
          >
            {rootAllowed && <option value="">{t("site.noParent")}</option>}
            {!rootAllowed && !parentId && <option value="">{t("form.select")}</option>}
            {parents.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </FormField>
      </div>
      <FormField id="site-address" label={t("site.address")} optional>
        <input id="site-address" value={address} onChange={(e) => setAddress(e.target.value)} autoComplete="off" />
      </FormField>
      <div className="form-row">
        <FormField id="site-lat" label={t("site.lat")} optional error={shown("lat")} hint={t("site.latHint")}>
          <input {...fieldAria("site-lat", { error: shown("lat"), hint: true })} inputMode="decimal" className="mono" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="40.7654" />
        </FormField>
        <FormField id="site-lon" label={t("site.lon")} optional error={shown("lon")} hint={t("site.lonHint")}>
          <input {...fieldAria("site-lon", { error: shown("lon"), hint: true })} inputMode="decimal" className="mono" value={lon} onChange={(e) => setLon(e.target.value)} placeholder="29.9432" />
        </FormField>
      </div>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

export function DeleteSiteDialog({ data, site, onClose }: { data: TenantData; site: Site; onClose: () => void }) {
  const { t } = useI18n();
  const { provider, refresh } = useSession();
  const hasChildren = data.sites.some((s) => s.parentId === site.id);
  const hasVehicles = data.vehicles.some((v) => v.siteId === site.id);

  if (hasChildren || hasVehicles) {
    return (
      <Dialog
        title={t("siteDelete.title", { name: site.name })}
        onClose={onClose}
        footer={
          <button type="button" className="button button-primary" onClick={onClose} data-autofocus>
            {t("common.ok")}
          </button>
        }
      >
        <Note>{hasChildren ? t("siteDelete.hasChildren") : t("siteDelete.hasVehicles")}</Note>
      </Dialog>
    );
  }
  return (
    <ConfirmDialog
      title={t("siteDelete.title", { name: site.name })}
      confirmLabel={t("common.delete")}
      danger
      successMessage={t("toast.deletedNamed", { name: site.name })}
      onClose={onClose}
      onConfirm={async () => {
        await provider.deleteSite(site.id);
        navigate(site.parentId ? `/sites/${encodeURIComponent(site.parentId)}` : "/sites");
        await refresh();
        onClose();
      }}
    >
      <p>{t("siteDelete.body")}</p>
    </ConfirmDialog>
  );
}
